import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import './SupplierConfig.css';

// Initialiser Modal pour l'accessibilité
Modal.setAppElement('#root');

function SupplierConfig({ stockData, onSuppliersConfigured }) {
  const [suppliers, setSuppliers] = useState([]);
  const [productSuppliers, setProductSuppliers] = useState({});
  const [newSupplierName, setNewSupplierName] = useState('');
  const [newSupplierLeadTime, setNewSupplierLeadTime] = useState(14);
  const [newSupplierAddress, setNewSupplierAddress] = useState('');
  const [newSupplierWechat, setNewSupplierWechat] = useState('');

  // États pour la modale d'édition d'adresse
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [currentAddress, setCurrentAddress] = useState('');

  // Identifie la clé SKU dans les données
  const getSkuKey = () => {
    if (!stockData || stockData.length === 0) return null;
    return 'sku' in stockData[0] ? 'sku' : 
           'SKU' in stockData[0] ? 'SKU' : 
           Object.keys(stockData[0])[0];
  };

  const skuKey = getSkuKey();

  // Filtrer les produits avec stock > 0 uniquement (cohérent avec StockTable.js)
  const getActiveProducts = () => {
    if (!stockData || stockData.length === 0) return [];
    
    return stockData.filter(item => {
      const totalStock = (parseInt(item.fbaQuantity) || 0) +
                        (parseInt(item.fbmQuantity) || 0) +
                        (parseInt(item.fcTransfer) || 0) +
                        (parseInt(item.homeQuantity) || 0) +
                        (parseInt(item.transitQuantity) || 0);
      return totalStock > 0; // Seuls les SKUs avec stock actif
    });
  };

  const activeProducts = getActiveProducts();

  // Ajouter un nouveau fournisseur
  const addSupplier = async () => {
    if (newSupplierName.trim() === '') return;

    try {
      const response = await fetch('/api/suppliers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newSupplierName.trim(),
          lead_time: parseInt(newSupplierLeadTime) || 14,
          address: newSupplierAddress.trim() || undefined,
          wechat: newSupplierWechat.trim() || undefined
        }),
      });

      if (response.ok) {
        // Recharger la configuration depuis l'API
        await loadConfiguration();
        setNewSupplierName('');
        setNewSupplierLeadTime(14);
        setNewSupplierAddress('');
        setNewSupplierWechat('');
      } else {
        console.error('Erreur lors de l\'ajout du fournisseur');
        alert('Erreur lors de l\'ajout du fournisseur');
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout du fournisseur :', error);
      alert('Erreur lors de l\'ajout du fournisseur');
    }
  };

  // Supprimer un fournisseur
  const removeSupplier = async (id) => {
    const supplier = suppliers.find(s => s.id === id);
    const supplierName = supplier ? supplier.name : 'ce fournisseur';
    
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le fournisseur "${supplierName}" ?\n\nATTENTION: Cette action ne peut pas être annulée.`)) {
      return;
    }
    
    try {
      const response = await fetch(`/api/suppliers/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message || 'Fournisseur supprimé avec succès');
        
        // Recharger la configuration
        await loadConfiguration();
      } else {
        const errorData = await response.json();
        
        if (response.status === 400 && errorData.assignedProducts) {
          const productsList = errorData.assignedProducts.join(', ');
          alert(`Impossible de supprimer le fournisseur.\n\n${errorData.error}\n\nProduits concernés: ${productsList}`);
        } else {
          alert(errorData.error || 'Erreur lors de la suppression du fournisseur');
        }
      }
    } catch (error) {
      console.error('Erreur lors de la suppression du fournisseur :', error);
      alert('Erreur lors de la suppression du fournisseur');
    }
  };

  // Modifier un champ d'un fournisseur
  const updateSupplierField = async (id, field, value) => {
    const supplier = suppliers.find(s => s.id === id);
    if (!supplier) return;

    try {
      const response = await fetch(`/api/suppliers/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: supplier.name,
          lead_time: field === 'leadTime' ? parseInt(value) || 0 : supplier.leadTime,
          address: field === 'address' ? value : supplier.address,
          wechat: field === 'wechat' ? value : supplier.wechat
        }),
      });

      if (response.ok) {
        // Recharger la configuration depuis l'API
        await loadConfiguration();
      } else {
        console.error(`Erreur lors de la mise à jour du champ ${field}`);
        alert(`Erreur lors de la mise à jour du champ ${field}`);
      }
    } catch (error) {
      console.error(`Erreur lors de la mise à jour du champ ${field}:`, error);
      alert(`Erreur lors de la mise à jour du champ ${field}`);
    }
  };

  // Alias pour compatibilité avec le code existant
  const updateSupplierLeadTime = (id, leadTime) => {
    updateSupplierField(id, 'leadTime', leadTime);
  };

  // Gestion de la modale d'édition d'adresse
  const openModal = (supplier) => {
    setEditingSupplier(supplier);
    setCurrentAddress(supplier.address || '');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingSupplier(null);
    setCurrentAddress('');
  };

  const handleSaveAddress = async () => {
    if (!editingSupplier) return;

    try {
      const response = await fetch(`/api/suppliers/${editingSupplier.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: editingSupplier.name,
          lead_time: editingSupplier.leadTime,
          address: currentAddress,
          wechat: editingSupplier.wechat
        }),
      });

      if (response.ok) {
        // Recharger la configuration depuis l'API
        await loadConfiguration();
        closeModal();
      } else {
        console.error('Erreur lors de la mise à jour de l\'adresse');
        alert('Erreur lors de la mise à jour de l\'adresse');
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'adresse:', error);
      alert('Erreur lors de la mise à jour de l\'adresse');
    }
  };

  // Associer un produit à un fournisseur
  const assignSupplier = async (sku, supplierId) => {
    try {
      const response = await fetch(`/api/products/${sku}/supplier`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          supplier_id: supplierId ? parseInt(supplierId) : null
        }),
      });

      if (response.ok) {
        // Mettre à jour l'état local
        if (supplierId) {
          setProductSuppliers({
            ...productSuppliers,
            [sku]: parseInt(supplierId)
          });
        } else {
          const updated = { ...productSuppliers };
          delete updated[sku];
          setProductSuppliers(updated);
        }
      } else {
        console.error('Erreur lors de l\'assignation du fournisseur');
        alert('Erreur lors de l\'assignation du fournisseur');
      }
    } catch (error) {
      console.error('Erreur lors de l\'assignation du fournisseur :', error);
      alert('Erreur lors de l\'assignation du fournisseur');
    }
  };

  // Sauvegarder la configuration (notification au composant parent)
  const saveConfiguration = async () => {
    try {
      // Recharger les données actuelles depuis l'API
      await loadConfiguration();
      
      // Préparer les données pour le composant parent
      const configData = {
        suppliers: suppliers,
        productSuppliers: productSuppliers
      };
      
      // Notifier le composant parent
      if (onSuppliersConfigured) {
        onSuppliersConfigured(configData);
      }
      
      alert('Configuration sauvegardée avec succès !');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde :', error);
      alert('Erreur lors de la sauvegarde de la configuration');
    }
  };

  // Charger la configuration depuis l'API au démarrage
  useEffect(() => {
    loadConfiguration();
  }, []);

  // Charger la configuration depuis l'API
  const loadConfiguration = async () => {
    try {
      // Migrer les données localStorage vers API si elles existent
      await migrateLocalStorageData();
      
      // Charger les fournisseurs depuis l'API
      const suppliersResponse = await fetch('/api/suppliers');
      if (suppliersResponse.ok) {
        const suppliersData = await suppliersResponse.json();
        // Mapper les données API vers le format du composant
        const mappedSuppliers = suppliersData.map(supplier => ({
          id: supplier.id,
          name: supplier.name,
          leadTime: supplier.lead_time,
          address: supplier.address || '',
          wechat: supplier.wechat || ''
        }));
        setSuppliers(mappedSuppliers);
      }
      
      // Charger les assignations produit-fournisseur
      const productsResponse = await fetch('/api/products');
      if (productsResponse.ok) {
        const productsData = await productsResponse.json();
        const assignments = {};
        productsData.forEach(product => {
          if (product.supplier_id) {
            assignments[product.sku] = product.supplier_id;
          }
        });
        setProductSuppliers(assignments);
      }
      
    } catch (error) {
      console.error('Erreur lors du chargement de la configuration :', error);
    }
  };

  // Migrer les données localStorage vers API (one-time migration)
  const migrateLocalStorageData = async () => {
    const savedConfig = localStorage.getItem('supplierConfig');
    if (savedConfig) {
      try {
        const config = JSON.parse(savedConfig);
        let migrated = 0;
        
        // Migrer les fournisseurs
        if (config.suppliers) {
          for (const supplier of config.suppliers) {
            try {
              await fetch('/api/suppliers', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  name: supplier.name,
                  lead_time: supplier.leadTime || 14
                }),
              });
              migrated++;
            } catch (error) {
              console.error(`Erreur migration fournisseur ${supplier.name}:`, error);
            }
          }
        }
        
        // Migrer les assignations produit-fournisseur
        if (config.productSuppliers) {
          for (const [sku, supplierId] of Object.entries(config.productSuppliers)) {
            try {
              await fetch(`/api/products/${sku}/supplier`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ supplier_id: parseInt(supplierId) }),
              });
            } catch (error) {
              console.error(`Erreur migration assignation ${sku}:`, error);
            }
          }
        }
        
        if (migrated > 0) {
          console.log(`Migration réussie: ${migrated} fournisseurs migrés vers la base de données`);
          // Supprimer localStorage après migration réussie
          localStorage.removeItem('supplierConfig');
        }
      } catch (error) {
        console.error('Erreur lors de la migration des données fournisseurs:', error);
      }
    }
  };

  return (
    <div className="supplier-config">
      <h2>Configuration des fournisseurs</h2>
      
      <div className="config-section">
        <h3>Gérer les fournisseurs</h3>
        
        <div className="add-supplier">
          <input
            type="text"
            placeholder="Nom du fournisseur"
            value={newSupplierName}
            onChange={(e) => setNewSupplierName(e.target.value)}
          />
          <input
            type="number"
            placeholder="Délai (jours)"
            value={newSupplierLeadTime}
            onChange={(e) => setNewSupplierLeadTime(e.target.value)}
            min="1"
          />
          <input
            type="text"
            placeholder="Adresse"
            value={newSupplierAddress}
            onChange={(e) => setNewSupplierAddress(e.target.value)}
          />
          <input
            type="text"
            placeholder="WeChat"
            value={newSupplierWechat}
            onChange={(e) => setNewSupplierWechat(e.target.value)}
          />
          <button onClick={addSupplier}>Ajouter</button>
        </div>
        
        {suppliers && suppliers.length > 0 ? (
          <table className="suppliers-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Délai de livraison (jours)</th>
                <th>Adresse</th>
                <th>WeChat</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map(supplier => (
                <tr key={supplier.id}>
                  <td>{supplier.name}</td>
                  <td>
                    <input
                      type="number"
                      value={supplier.leadTime}
                      onChange={(e) => updateSupplierLeadTime(supplier.id, e.target.value)}
                      min="1"
                    />
                  </td>
                  <td>
                    <span
                      onClick={() => openModal(supplier)}
                      style={{
                        cursor: 'pointer',
                        color: supplier.address ? '#333' : '#999',
                        textDecoration: 'underline',
                        fontStyle: supplier.address ? 'normal' : 'italic'
                      }}
                      title={supplier.address || 'Cliquer pour ajouter une adresse'}
                    >
                      {supplier.address
                        ? (supplier.address.length > 30
                          ? supplier.address.substring(0, 30) + '...'
                          : supplier.address)
                        : 'Ajouter une adresse'}
                    </span>
                  </td>
                  <td>
                    <input
                      type="text"
                      className="wechat-input"
                      value={supplier.wechat}
                      onChange={(e) => updateSupplierField(supplier.id, 'wechat', e.target.value)}
                      placeholder="WeChat"
                    />
                  </td>
                  <td>
                    <button
                      className="remove-btn"
                      onClick={() => removeSupplier(supplier.id)}
                      style={{
                        backgroundColor: '#ff4757',
                        color: 'white',
                        padding: '8px 12px',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      🗑️ Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            <p>Aucun fournisseur trouvé. Ajoutez un fournisseur pour commencer.</p>
          </div>
        )}
      </div>
      
      {activeProducts && activeProducts.length > 0 && skuKey && (
        <div className="config-section">
          <h3>Associer les produits aux fournisseurs ({activeProducts.length} produits actifs)</h3>
          
          <div className="product-list">
            <table className="product-supplier-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Fournisseur</th>
                </tr>
              </thead>
              <tbody>
                {activeProducts.map((item, index) => (
                  <tr key={index}>
                    <td>{item[skuKey]}</td>
                    <td>
                      <select
                        value={productSuppliers[item[skuKey]] || ''}
                        onChange={(e) => assignSupplier(item[skuKey], e.target.value)}
                      >
                        <option value="">Sélectionner un fournisseur</option>
                        {suppliers.map(supplier => (
                          <option key={supplier.id} value={supplier.id}>
                            {supplier.name} ({supplier.leadTime} jours)
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      <div className="actions">
        <button className="save-btn" onClick={saveConfiguration}>
          Sauvegarder la configuration
        </button>
      </div>

      {/* Modale pour l'édition de l'adresse */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        contentLabel="Modifier l'adresse"
        style={{
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            width: '500px',
            maxWidth: '90%',
            padding: '30px',
            borderRadius: '8px'
          },
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }
        }}
      >
        <h2 style={{ marginTop: 0, marginBottom: '20px' }}>
          Modifier l'adresse de {editingSupplier?.name}
        </h2>
        <textarea
          value={currentAddress}
          onChange={(e) => setCurrentAddress(e.target.value)}
          rows="5"
          style={{
            width: '100%',
            padding: '10px',
            fontSize: '14px',
            borderRadius: '4px',
            border: '1px solid #ddd',
            marginBottom: '20px',
            fontFamily: 'inherit',
            resize: 'vertical'
          }}
          placeholder="Saisissez l'adresse complète du fournisseur..."
        />
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            onClick={closeModal}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Annuler
          </button>
          <button
            onClick={handleSaveAddress}
            style={{
              padding: '10px 20px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Enregistrer
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default SupplierConfig;